import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { ScrollToTop } from './components/common/ScrollToTop';
import { GlobalLoadingOverlay } from './components/elements/GlobalLoadingOverlay';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { UserProvider } from './contexts/UserContext';
import { useNetworkCheck } from './hooks/useNetworkCheck';
import { Routing } from './provider/Routing';
import { isStaffPage } from '@/utils/isStaffPage';
import { parseSubdomain } from '@/utils/subdomainParser';

// CSSファイルを事前にglobで登録（ビルド時に確実に含まれる）
const cssModules = import.meta.glob<{ default: string }>(
  ['./*.css', '!./index.css'],
  { eager: false },
);

// 共有端末と個人端末でCSSを切り替えている
function CssLoader() {
  const location = useLocation();
  // サブドメインの有無で共有端末かどうかを判定
  const isShareDevicePage = parseSubdomain(window.location.hostname) !== null;
  // スタッフ専用ページかどうかを判定
  const isStaffOnlyPage = isStaffPage(location.pathname);

  useEffect(() => {
    let cssPath: string;
    if (isStaffOnlyPage) {
      cssPath = './StaffOnly.css';
    } else if (isShareDevicePage) {
      cssPath = './ShareDevice.css';
    } else {
      cssPath = './PersonalDevice.css';
    }
    void cssModules[cssPath]?.();
  }, [isShareDevicePage, isStaffOnlyPage]);

  return null;
}

function App() {
  useNetworkCheck();
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <BrowserRouter>
        <CssLoader />
        <ScrollToTop />
        <LoadingProvider>
          <GlobalLoadingOverlay />
          <AuthProvider>
            <UserProvider>
              <Routing />
            </UserProvider>
          </AuthProvider>
        </LoadingProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}) {
  const { t } = useTranslation();
  const errorMessage =
    error instanceof Error
      ? error.message
      : t('errors.errorBoundary.defaultMessage');
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <h2>{t('errors.errorBoundary.title')}</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>{errorMessage}</p>
      <button
        onClick={resetError}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          cursor: 'pointer',
        }}
      >
        {t('errors.errorBoundary.retryButton')}
      </button>
    </div>
  );
}

export default App;
